import { RaverAI, RaverState, RaverContext } from './RaverAI';

/**
 * A single raver entity with position, AI state, and needs.
 * Pure data + logic, Phaser sprite managed externally by CrowdManager.
 */
export class Raver {
  readonly id: string;
  readonly ai: RaverAI;
  readonly colorIndex: number;

  col: number;
  row: number;
  targetCol: number;
  targetRow: number;

  thirst = 0;
  satisfaction = 0;
  ticksAtVenue = 0;
  drinksPurchased = 0;

  private static THIRST_RATE = 0.02; // per sim tick
  private static DRINK_QUENCH_RATE = 0.5;

  constructor(id: string, entryCol: number, entryRow: number, colorIndex: number) {
    this.id = id;
    this.ai = new RaverAI();
    this.colorIndex = colorIndex;
    this.col = entryCol;
    this.row = entryRow;
    this.targetCol = entryCol;
    this.targetRow = entryRow;
  }

  get state(): RaverState {
    return this.ai.state;
  }

  setTarget(col: number, row: number): void {
    this.targetCol = col;
    this.targetRow = row;
  }

  get isAtTarget(): boolean {
    return this.col === this.targetCol && this.row === this.targetRow;
  }

  /** Move one step toward target */
  moveTowardTarget(): void {
    if (this.col < this.targetCol) this.col++;
    else if (this.col > this.targetCol) this.col--;

    if (this.row < this.targetRow) this.row++;
    else if (this.row > this.targetRow) this.row--;
  }

  /** Tick the raver's simulation */
  tick(cellSatisfaction: number, nearBar: boolean, atBar: boolean): void {
    this.ticksAtVenue++;
    this.satisfaction = cellSatisfaction;

    // Thirst management
    if (this.ai.state === RaverState.DANCING) {
      this.thirst = Math.min(1, this.thirst + Raver.THIRST_RATE);
    }
    if (this.ai.state === RaverState.DRINKING) {
      this.thirst = Math.max(0, this.thirst - Raver.DRINK_QUENCH_RATE);
      if (this.thirst <= 0) {
        this.drinksPurchased++;
      }
    }

    // Move toward target if not there
    if (!this.isAtTarget) {
      this.moveTowardTarget();
    }

    // Update AI
    const ctx: RaverContext = {
      hasTarget: true,
      atTarget: this.isAtTarget,
      satisfaction: this.satisfaction,
      thirst: this.thirst,
      nearBar,
      atBar,
    };
    this.ai.update(ctx);
  }

  get isLeaving(): boolean {
    return this.ai.state === RaverState.LEAVING;
  }
}
